import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * VideoModal
 *
 * Reusable Vimeo-embed modal. Visual + behaviour intentionally matches
 * the homepage teaser modal so the experience is consistent across the
 * site (HowItWorks "Watch presentation", any other Watch buttons, etc.).
 *
 * Behaviour:
 *   - Tap the dimmed backdrop → close.
 *   - Tap the X button → close.
 *   - Press Escape → close.
 *   - Body scroll is locked while open.
 *
 * Layout:
 *   - Phones (portrait + landscape): X is anchored to the TOP-RIGHT of
 *     the SCREEN so it's never on or overlapping the video.
 *   - Tablets / desktops (≥ lg, 1024px+): X floats just above the
 *     top-right corner of the video frame.
 */
const VideoModal = ({ isOpen, onClose, videoUrl, title }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 lg:p-10 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="video-modal"
    >
      {/* Phone close button — top-right of the screen, far from the video. */}
      <button
        type="button"
        aria-label="Close video"
        onClick={onClose}
        data-testid="video-modal-close"
        className="absolute top-4 right-4 lg:top-auto lg:right-auto z-10 w-11 h-11 inline-flex items-center justify-center rounded-full bg-white/90 text-[#0b1f3a] hover:bg-white shadow-lg transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Tablet/desktop close button — floats above the video. */}
        <button
          type="button"
          aria-label="Close video"
          onClick={onClose}
          className="hidden lg:inline-flex absolute -top-14 right-0 z-10 w-11 h-11 items-center justify-center rounded-full bg-white/90 text-[#0b1f3a] hover:bg-white shadow-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
          {/*
            Two rendering modes, auto-picked from the URL:
              - Direct MP4 file (BunnyCDN, self-hosted, etc.) → native
                HTML5 <video> tag with the browser's built-in controls.
                Cheaper (no third-party player JS), faster to first-frame,
                and immune to third-party privacy/domain-restriction
                blocks that occasionally break Vimeo embeds.
              - Anything else (Vimeo, YouTube-nocookie, etc.) → iframe
                with Vimeo's autoplay + clean-chrome query params, kept
                for backward compatibility if we ever route a Vimeo URL
                through this modal again.
          */}
          {/\.mp4($|\?)/i.test(videoUrl) ? (
            <video
              src={videoUrl}
              title={title}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full bg-black"
            />
          ) : (
            <iframe
              src={`${videoUrl}?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&sidedock=0`}
              title={title}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
